import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { 
  GraduationCap, Briefcase, CheckCircle2, Clock, 
  ChevronDown, ChevronUp, Save, Send,
  History, TrendingUp, Calendar, User, MessageSquare,
  Star, AlertCircle
} from 'lucide-react';
import { Label } from './ui/label';
import api from '../services/api';
import CompetencesRadarChart from './CompetencesRadarChart';

const NIVEAUX = [
  { value: 'non_acquis', label: 'Non acquis', color: 'bg-red-100 text-red-700', icon: '1', stars: 1 },
  { value: 'en_cours', label: "En cours", color: 'bg-amber-100 text-amber-700', icon: '2', stars: 2 },
  { value: 'acquis', label: 'Acquis', color: 'bg-emerald-100 text-emerald-700', icon: '3', stars: 3 },
  { value: 'maitrise', label: 'Maîtrisé', color: 'bg-blue-100 text-blue-700', icon: '4', stars: 4 }
];

const CompetenceRow = ({ competence, categorie, evaluation, onChange, readOnly = false, showValidation = false }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getNiveauInfo = (value) => NIVEAUX.find(n => n.value === value) || NIVEAUX[0];
  const currentNiveau = getNiveauInfo(evaluation?.niveau_auto);
  const validatedNiveau = evaluation?.niveau_valide ? getNiveauInfo(evaluation?.niveau_valide) : null;

  return (
    <div className="border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between py-3 px-4 hover:bg-slate-50 transition-colors">
        <div className="flex-1 flex items-center gap-2">
          <span className="font-medium text-slate-900">{competence}</span>
          {validatedNiveau && (
            <Badge className={`${validatedNiveau.color}`}>
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Validé: {validatedNiveau.label}
            </Badge>
          )}
        </div>
        
        {readOnly ? (
          <div className="flex items-center gap-2">
            <Badge className={currentNiveau.color}>
              <div className="flex">
                {[1, 2, 3, 4].map(n => (
                  <Star 
                    key={n}
                    className={`w-3 h-3 ${n <= currentNiveau.stars ? 'fill-current' : 'text-slate-300'}`}
                  />
                ))}
              </div>
            </Badge>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {NIVEAUX.map(niveau => (
                <button
                  key={niveau.value}
                  onClick={() => onChange(competence, categorie, niveau.value)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all flex items-center justify-center ${
                    evaluation?.niveau_auto === niveau.value 
                      ? niveau.color + ' ring-2 ring-offset-1 ring-blue-500'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={niveau.label}
                  data-testid={`niveau-${competence}-${niveau.value}`}
                >
                  {niveau.stars}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {showValidation && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="ml-2"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        )}
      </div>
      
      {expanded && showValidation && (
        <div className="px-4 pb-3 bg-slate-50">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Validation animateur:</span>
            {NIVEAUX.map(niveau => (
              <button
                key={niveau.value}
                onClick={() => onChange(competence, categorie, evaluation?.niveau_auto, niveau.value)}
                className={`px-2 py-1 rounded text-xs ${
                  evaluation?.niveau_valide === niveau.value 
                    ? niveau.color + ' ring-1 ring-blue-500'
                    : 'bg-white hover:bg-slate-100'
                }`}
              >
                {niveau.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DiagnosticCompetences = ({ isValidator = false }) => {
  const [options, setOptions] = useState({ 
    competences_transversales: [], 
    competences_techniques: [], 
    competences_personnelles: [],
    niveaux: [] 
  });
  const [diagnostic, setDiagnostic] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTransversales, setShowTransversales] = useState(true);
  const [showTechniques, setShowTechniques] = useState(true);
  const [showPersonnelles, setShowPersonnelles] = useState(true);
  
  // Modal historique/évolution
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Modal mise à jour avec commentaire
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateComment, setUpdateComment] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [optionsRes, diagnosticRes] = await Promise.all([
        api.get('/diagnostic-competences/options'),
        api.get('/diagnostic-competences/me')
      ]);
      
      setOptions(optionsRes.data);
      
      if (diagnosticRes.data) {
        setDiagnostic(diagnosticRes.data);
        setEvaluations(diagnosticRes.data.evaluations || []);
      } else {
        // Initialiser les évaluations vides pour les 3 catégories
        const allCompetences = [
          ...optionsRes.data.competences_transversales.map(c => ({ competence: c, categorie: 'transversale' })),
          ...optionsRes.data.competences_techniques.map(c => ({ competence: c, categorie: 'technique' })),
          ...optionsRes.data.competences_personnelles.map(c => ({ competence: c, categorie: 'personnelle' }))
        ];
        setEvaluations(allCompetences.map(c => ({
          ...c,
          niveau_auto: null, // Pas de niveau par défaut, le bénéficiaire choisit
          niveau_valide: null
        })));
      }
    } catch (error) {
      console.error('Erreur chargement diagnostic:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (competence, categorie, niveauAuto, niveauValide = null) => {
    setEvaluations(prev => {
      const existing = prev.find(e => e.competence === competence);
      if (existing) {
        return prev.map(e => 
          e.competence === competence 
            ? { 
                ...e, 
                niveau_auto: niveauAuto,
                ...(niveauValide !== null ? { niveau_valide: niveauValide } : {})
              }
            : e
        );
      }
      return [...prev, { competence, categorie, niveau_auto: niveauAuto }];
    });
  };

  const handleSave = async (submit = false, commentaire = null) => {
    setSaving(true);
    try {
      if (diagnostic?.id) {
        // Mise à jour avec historique et commentaire
        await api.put(`/diagnostic-competences/${diagnostic.id}`, { 
          evaluations,
          statut: submit ? 'soumis' : 'en_cours',
          commentaire: commentaire || null
        });
      } else {
        // Création initiale
        await api.post('/diagnostic-competences', { evaluations });
      }
      toast.success(submit ? 'Diagnostic soumis pour validation' : 'Diagnostic sauvegardé');
      setShowUpdateModal(false);
      setUpdateComment('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const getEvaluation = (competence) => {
    return evaluations.find(e => e.competence === competence) || { niveau_auto: 'non_acquis' };
  };

  const getProgress = () => {
    const total = evaluations.length;
    const evaluated = evaluations.filter(e => e.niveau_auto && e.niveau_auto !== 'non_acquis').length;
    return total > 0 ? Math.round((evaluated / total) * 100) : 0;
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement du diagnostic...</p>
        </CardContent>
      </Card>
    );
  }

  const isReadOnly = diagnostic?.statut === 'valide';
  const isPending = diagnostic?.statut === 'soumis';

  return (
    <div className="space-y-6">
      {/* En-tête avec progression */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl shadow-sm border-0">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <GraduationCap className="w-7 h-7 inline mr-2 text-blue-600" />
                Diagnostic de Compétences
              </h2>
              <p className="text-slate-600 mt-1">
                Auto-évaluez vos compétences pour mieux cibler votre projet professionnel
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Bouton historique - visible si diagnostic existe avec historique */}
              {diagnostic?.historique?.length > 0 && (
                <Button
                  onClick={() => setShowHistoryModal(true)}
                  variant="outline"
                  className="rounded-xl bg-white"
                  data-testid="view-history-btn"
                >
                  <History className="w-4 h-4 mr-2" />
                  Évolution ({diagnostic.historique.length})
                </Button>
              )}
              
              {diagnostic?.statut === 'valide' && (
                <Badge className="bg-emerald-100 text-emerald-700 text-sm px-3 py-1">
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Validé par {diagnostic.valide_par_nom}
                </Badge>
              )}
              {diagnostic?.statut === 'soumis' && (
                <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">
                  <Clock className="w-4 h-4 mr-1" />
                  En attente de validation
                </Badge>
              )}
              {!diagnostic?.statut && (
                <Badge className="bg-slate-100 text-slate-600 text-sm px-3 py-1">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Non commencé
                </Badge>
              )}
            </div>
          </div>
          
          {/* Barre de progression */}
          <div className="mt-4">
            <div className="flex justify-between text-sm text-slate-600 mb-1">
              <span>Progression</span>
              <span>{getProgress()}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${getProgress()}%` }}
              ></div>
            </div>
          </div>

          {/* Légende des niveaux */}
          <div className="mt-4 flex flex-wrap gap-2">
            {NIVEAUX.map(niveau => (
              <Badge key={niveau.value} className={`${niveau.color} text-xs`}>
                {niveau.stars} = {niveau.label}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Graphique Radar des Compétences */}
      {evaluations.length > 0 && (
        <CompetencesRadarChart 
          evaluations={evaluations} 
          options={options}
        />
      )}

      {/* Compétences Transversales (Soft Skills) */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors rounded-t-3xl"
          onClick={() => setShowTransversales(!showTransversales)}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-purple-600" />
              Compétences Transversales (Savoir-être)
              <Badge className="bg-slate-100 text-slate-600 ml-2">
                {options.competences_transversales?.length || 0}
              </Badge>
            </span>
            {showTransversales ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {showTransversales && (
          <CardContent className="p-0">
            {(options.competences_transversales || []).map(comp => (
              <CompetenceRow
                key={comp}
                competence={comp}
                categorie="transversale"
                evaluation={getEvaluation(comp)}
                onChange={handleChange}
                readOnly={isReadOnly}
                showValidation={isValidator}
              />
            ))}
          </CardContent>
        )}
      </Card>

      {/* Compétences Techniques (Hard Skills) */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setShowTechniques(!showTechniques)}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600" />
              Compétences Techniques (Savoir-faire)
              <Badge className="bg-slate-100 text-slate-600 ml-2">
                {options.competences_techniques?.length || 0}
              </Badge>
            </span>
            {showTechniques ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {showTechniques && (
          <CardContent className="p-0">
            {(options.competences_techniques || []).map(comp => (
              <CompetenceRow
                key={comp}
                competence={comp}
                categorie="technique"
                evaluation={getEvaluation(comp)}
                onChange={handleChange}
                readOnly={isReadOnly}
                showValidation={isValidator}
              />
            ))}
          </CardContent>
        )}
      </Card>

      {/* Compétences Personnelles */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setShowPersonnelles(!showPersonnelles)}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Compétences Personnelles (Savoir-devenir)
              <Badge className="bg-slate-100 text-slate-600 ml-2">
                {options.competences_personnelles?.length || 0}
              </Badge>
            </span>
            {showPersonnelles ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {showPersonnelles && (
          <CardContent className="p-0">
            {(options.competences_personnelles || []).map(comp => (
              <CompetenceRow
                key={comp}
                competence={comp}
                categorie="personnelle"
                evaluation={getEvaluation(comp)}
                onChange={handleChange}
                readOnly={isReadOnly}
                showValidation={isValidator}
              />
            ))}
          </CardContent>
        )}
      </Card>

      {/* Boutons d'action */}
      {!isReadOnly && (
        <div className="flex justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowUpdateModal(true)}
            disabled={saving}
            className="rounded-xl border-amber-500 text-amber-600 hover:bg-amber-50"
            data-testid="btn-update-with-comment"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Modifier avec commentaire
          </Button>
          <Button 
            variant="outline" 
            onClick={() => handleSave(false)}
            disabled={saving}
            className="rounded-xl"
          >
            <Save className="w-4 h-4 mr-2" />
            Sauvegarder
          </Button>
          <Button 
            onClick={() => handleSave(true)}
            disabled={saving || isPending}
            className="bg-blue-600 hover:bg-blue-700 rounded-xl"
          >
            <Send className="w-4 h-4 mr-2" />
            {isPending ? 'En attente de validation' : 'Soumettre pour validation'}
          </Button>
        </div>
      )}

      {/* Modal Historique / Évolution */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              Évolution de vos Compétences
            </DialogTitle>
            <DialogDescription>
              Suivez l'historique de vos modifications et validations
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Résumé */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-0">
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{diagnostic?.historique?.length || 0}</p>
                    <p className="text-sm text-slate-600">Modifications</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">
                      {diagnostic?.historique?.filter(h => h.action === 'validation').length || 0}
                    </p>
                    <p className="text-sm text-slate-600">Validations</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">
                      {diagnostic?.date_validation 
                        ? new Date(diagnostic.date_validation).toLocaleDateString('fr-FR')
                        : '-'
                      }
                    </p>
                    <p className="text-sm text-slate-600">Dernière validation</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
              
              {diagnostic?.historique?.slice().reverse().map((entry, index) => {
                const actionColors = {
                  'mise_a_jour_evaluations': 'bg-blue-500',
                  'validation': 'bg-emerald-500',
                  'rejet': 'bg-red-500',
                  'modification': 'bg-amber-500'
                };
                const actionLabels = {
                  'mise_a_jour_evaluations': 'Mise à jour des évaluations',
                  'validation': 'Validation du diagnostic',
                  'rejet': 'Diagnostic rejeté',
                  'modification': 'Modification'
                };
                const actionIcons = {
                  'mise_a_jour_evaluations': <Save className="w-4 h-4" />,
                  'validation': <CheckCircle2 className="w-4 h-4" />,
                  'rejet': <AlertCircle className="w-4 h-4" />,
                  'modification': <History className="w-4 h-4" />
                };
                
                return (
                  <div key={index} className="relative pl-10 pb-6 last:pb-0">
                    {/* Point sur la timeline */}
                    <div className={`absolute left-2 w-5 h-5 rounded-full ${actionColors[entry.action] || 'bg-slate-400'} flex items-center justify-center text-white`}>
                      {actionIcons[entry.action] || <History className="w-3 h-3" />}
                    </div>
                    
                    {/* Contenu */}
                    <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold text-slate-900">
                              {actionLabels[entry.action] || entry.action}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(entry.date).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {entry.modifie_par_nom}
                              </span>
                            </div>
                          </div>
                          <Badge className={`${
                            entry.action === 'validation' ? 'bg-emerald-100 text-emerald-700' :
                            entry.action === 'rejet' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {entry.modifie_par_role === 'animateur' ? 'Intervenant' :
                             entry.modifie_par_role === 'admin' ? 'Admin' :
                             entry.modifie_par_role === 'bénéficiaire' ? 'Bénéficiaire' :
                             entry.modifie_par_role}
                          </Badge>
                        </div>
                        
                        {/* Afficher les compétences modifiées si disponibles */}
                        {entry.evaluations_avant && entry.evaluations_avant.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-xs text-slate-500 mb-2">Compétences évaluées :</p>
                            <div className="flex flex-wrap gap-1">
                              {entry.evaluations_avant.slice(0, 5).map((eval_item, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {eval_item.competence}
                                </Badge>
                              ))}
                              {entry.evaluations_avant.length > 5 && (
                                <Badge variant="outline" className="text-xs bg-slate-50">
                                  +{entry.evaluations_avant.length - 5} autres
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Nouveau statut */}
                        {entry.nouveau_statut && (
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-xs text-slate-500">
                              Nouveau statut : <span className="font-medium text-slate-700">{
                                entry.nouveau_statut === 'valide' ? '✅ Validé' :
                                entry.nouveau_statut === 'soumis' ? '📤 Soumis' :
                                entry.nouveau_statut === 'rejete' ? '❌ Rejeté' :
                                entry.nouveau_statut
                              }</span>
                            </p>
                          </div>
                        )}

                        {/* Commentaire */}
                        {entry.commentaire && (
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-xs text-slate-500 mb-1">💬 Commentaire :</p>
                            <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded-lg italic">
                              "{entry.commentaire}"
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
              
              {(!diagnostic?.historique || diagnostic.historique.length === 0) && (
                <div className="text-center py-8 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Aucun historique disponible</p>
                  <p className="text-sm">Les modifications apparaîtront ici</p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowHistoryModal(false)} className="rounded-xl">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Mise à jour avec commentaire */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <MessageSquare className="w-6 h-6 text-amber-600" />
              Modifier avec commentaire
            </DialogTitle>
            <DialogDescription>
              Enregistrez vos modifications avec un commentaire explicatif. L'horodatage sera automatiquement ajouté à l'historique.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2 text-slate-600 mb-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Horodatage automatique</span>
              </div>
              <p className="text-sm text-slate-500">
                La date et l'heure de modification seront automatiquement enregistrées : <br/>
                <span className="font-semibold text-slate-700">
                  {new Date().toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </p>
            </div>

            <div className="space-y-2">
              <Label className="font-semibold">Commentaire de modification</Label>
              <Textarea
                value={updateComment}
                onChange={(e) => setUpdateComment(e.target.value)}
                placeholder="Décrivez les modifications apportées... (ex: Mise à jour suite à la formation Excel du 15/03)"
                className="rounded-xl min-h-[120px]"
                data-testid="update-comment-input"
              />
              <p className="text-xs text-slate-500">
                Ce commentaire sera visible dans l'historique des modifications
              </p>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center gap-2 text-blue-700 mb-2">
                <Save className="w-4 h-4" />
                <span className="text-sm font-semibold">Compétences à sauvegarder</span>
              </div>
              <p className="text-sm text-blue-600">
                {evaluations.filter(e => e.niveau_auto).length} compétences seront mises à jour avec vos évaluations actuelles.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowUpdateModal(false);
                setUpdateComment('');
              }} 
              className="rounded-xl"
            >
              Annuler
            </Button>
            <Button 
              onClick={() => handleSave(false, updateComment)}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 rounded-xl"
              data-testid="confirm-update-btn"
            >
              {saving ? (
                <>Enregistrement...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer avec commentaire
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiagnosticCompetences;
